import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { E2eIdDirective } from '../directives/e2e-id.directive';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';

/** Data contract for {@link RecoveryCodesDialogComponent}. */
export interface RecoveryCodesDialogData
{
    codes: string[];
}

/**
 * Standalone show-once recovery-codes dialog (SD-8082) for the Settings **regenerate** path.
 *
 * A thin shell around {@link RecoveryCodesPanelComponent}: header, the panel, and the confirm
 * action gated on the panel's acknowledgement. Enrollment does NOT use this dialog — the setup
 * steps embed the same panel as their final stage, so the codes never open a second surface on
 * top of the setup one.
 *
 * The plaintext codes are returned by the server exactly once, so this is a must-acknowledge
 * gate: `disableClose` plus the panel's "I have saved these" checkbox before Done is enabled.
 */
@Component({
    selector: 'app-recovery-codes-dialog',
    templateUrl: './recovery-codes-dialog.component.html',
    styleUrls: ['./recovery-codes-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatDialogModule,
        RecoveryCodesPanelComponent,
        E2eIdDirective,
        FlexSpacerDirective,
    ],
})
export class RecoveryCodesDialogComponent
{
    /**
     * Open the show-once codes dialog. Non-dismissable — the codes are shown only once, so the
     * user must acknowledge before it can close. Callers do not need the result.
     */
    static open(dialog: MatDialog, codes: string[]): MatDialogRef<RecoveryCodesDialogComponent, void> {
        return dialog.open<RecoveryCodesDialogComponent, RecoveryCodesDialogData, void>(
                RecoveryCodesDialogComponent,
                {
                    // Shares the 2FA flow surface styling (radius + blurred backdrop) so it looks
                    // right on the auth-layout overlay where the enrollment gate opens it.
                    panelClass: ['v2-dialog', 'two-factor-flow-dialog'],
                    backdropClass: 'two-factor-flow-backdrop',
                    width: MatDialogSizes.MD,
                    disableClose: true,
                    data: {codes},
                },
        );
    }

    readonly codes: string[];

    /** The confirm button stays disabled until the user affirms they've saved the codes. */
    acknowledged = false;

    constructor(
            private _dialogRef: MatDialogRef<RecoveryCodesDialogComponent, void>,
            @Inject(MAT_DIALOG_DATA) public data: RecoveryCodesDialogData,
    ) {
        this.codes = data?.codes ?? [];
    }

    /** Close only once the user has confirmed they saved the codes. */
    done(): void {
        if (!this.acknowledged) {
            return;
        }
        this._dialogRef.close();
    }
}
